import * as React from 'react';
import * as mobx from 'mobx';
import { RequiredPick } from '../../../infrastructure/type-extensions';
import { ISubWindowParameters } from '../../../infrastructure/sub-windows';

interface ISubWindowViewModel {
    closeSubWindow?: () => void;
    changeState?: (requestedState: SubWindowState) => void;
    isClosed: () => boolean;
    stateFlag: SubWindowState;
    // disable no-any because sub-windows can be called with custom parameters types
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getParameters: () => Readonly<ISubWindowParametersWithRequiredLayouts<any>>;
}

/**
 * Describe type equivalent to ISubWindowParameters but with layouts not being optional.
 * In is used internally in the SubWindowViewModel as layouts have to be defined by the time, parameters passed in this viewmodel.
 */
export type ISubWindowParametersWithRequiredLayouts<T> = RequiredPick<ISubWindowParameters<T>, 'minimizedLayoutComponent' | 'layoutComponent'>;

/**
 * Viewmodel, used by the particular sub-window internally for storing it's parameters and managing it's state.
 * Is not exposed to external code (see ISubWindowFacade to check what IS exposed).
 */

export enum SubWindowState {
    MINIMIZED,
    MAXIMIZED,
    EXPANDED
}
class SubWindowViewModel implements ISubWindowViewModel {
    @mobx.observable
    public stateFlag: SubWindowState = SubWindowState.MAXIMIZED;

    @mobx.observable
    private isClosedFlag: boolean;

    /**
     * receive callbacks, supposed to be called when particular actions are bing made from within the sub-window
     * @param handleClose
     * @param handleMinimize
     * @param handleMaximize
     * @param parameters
     */
    constructor(
        private handleClose: () => void,
        private handleMinimize: () => void,
        private handleMaximize: () => void,
        // disable no-any because sub-windows can be called with custom parameters types
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        private parameters: ISubWindowParametersWithRequiredLayouts<any>,
    ) {
        if (parameters.isExpandedByDefault) this.stateFlag = SubWindowState.EXPANDED;
    }

    @mobx.action
    public closeSubWindow = () => {
        this.isClosedFlag = true;
        return this.handleClose();
    };

    public isClosed(): boolean {
        return this.isClosedFlag;
    }

    @mobx.action
    public changeState = (requestedState: SubWindowState) => {
        this.stateFlag = requestedState;

        if (this.stateFlag === SubWindowState.MINIMIZED) {
            this.handleMinimize();
        } else if (this.stateFlag === SubWindowState.MAXIMIZED) {
            this.handleMaximize();
        }
    };

    public getParameters() {
        return this.parameters;
    }
}

const SubWindowContext = React.createContext<ISubWindowViewModel>(null);

export {
    ISubWindowViewModel,
    SubWindowViewModel,
    SubWindowContext,
};
